import { addDays, format, isToday, isTomorrow, isYesterday, parseISO, startOfDay } from "date-fns";

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

function toOffsetIso(date: Date): string {
  const offsetMin = -date.getTimezoneOffset();
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/**
 * Status-matrix query for the day picker.
 * Today / current period: omit params (no `from`).
 * History: pass `to` as-of that day only — do not send a daily `from`+`to` span (that opens day columns).
 */
export function getMatrixQueryParams(day: Date, timeZone?: string | null): { to: string } | undefined {
  if (isToday(day)) return undefined;
  return { to: getDayWindow(day, timeZone).to };
}

/**
 * Board-local window for the calendar day the user picked (`day` is a local date, not an instant).
 * Prefer the board IANA timezone when provided; otherwise use the browser local zone.
 */
export function getDayWindow(day: Date, timeZone?: string | null): { from: string; to: string } {
  if (timeZone) {
    try {
      const dayKey = format(day, "yyyy-MM-dd");
      const from = wallTimeInZoneToIso(dayKey, "00:00:00.000", timeZone);
      const to = wallTimeInZoneToIso(dayKey, "23:59:59.999", timeZone);
      if (from && to) return { from, to };
    } catch {
      /* invalid timezone: use the browser's local day below */
    }
  }

  const localStart = startOfDay(day);
  const localEnd = new Date(localStart);
  localEnd.setHours(23, 59, 59, 999);
  return {
    from: toOffsetIso(localStart),
    to: toOffsetIso(localEnd)
  };
}

/** The board-calendar date of an instant, as a local date comparable with the picked `day`. */
export function boardDay(iso: string, timeZone?: string | null): Date {
  const at = parseISO(iso);
  if (!timeZone) return startOfDay(at);
  const { y, m, d } = formatWallParts(at, timeZone);
  return new Date(y, m - 1, d);
}

export function shiftDay(day: Date, delta: number): Date {
  return addDays(startOfDay(day), delta);
}

/** "Today" / "Sun, 27 Sep"; `withDate` adds the date to relative days ("Today, 30 Sep"). */
export function formatDayLabel(day: Date, withDate = false): string {
  const relative = isToday(day) ? "Today" : isYesterday(day) ? "Yesterday" : isTomorrow(day) ? "Tomorrow" : null;
  if (!relative) return format(day, "EEE, d MMM");
  return withDate ? `${relative}, ${format(day, "d MMM")}` : relative;
}

/**
 * Convert a wall-clock time in an IANA zone to an ISO string with that zone's offset.
 * Uses binary search over UTC instants that format to the desired local wall time.
 */
function wallTimeInZoneToIso(dayKey: string, time: string, timeZone: string): string | null {
  const [y, m, d] = dayKey.split("-").map(Number);
  const [hh, mm, ssMs] = time.split(":");
  const [ss, ms = "0"] = (ssMs ?? "0").split(".");
  const guess = Date.UTC(y!, (m ?? 1) - 1, d ?? 1, Number(hh), Number(mm), Number(ss), Number(ms));

  let lo = guess - 36 * 3600 * 1000;
  let hi = guess + 36 * 3600 * 1000;
  for (let i = 0; i < 40; i++) {
    const mid = Math.floor((lo + hi) / 2);
    const wall = formatWallParts(new Date(mid), timeZone);
    const cmp = compareWall(wall, { y: y!, m: m!, d: d!, hh: Number(hh), mm: Number(mm), ss: Number(ss), ms: Number(ms) });
    if (cmp === 0) return toOffsetIsoForZone(new Date(mid), timeZone);
    if (cmp < 0) lo = mid + 1;
    else hi = mid - 1;
  }
  return null;
}

function formatWallParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    fractionalSecondDigits: 3
  } as Intl.DateTimeFormatOptions).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    y: get("year"),
    m: get("month"),
    d: get("day"),
    hh: get("hour"),
    mm: get("minute"),
    ss: get("second"),
    ms: get("fractionalSecond")
  };
}

function compareWall(a: { y: number; m: number; d: number; hh: number; mm: number; ss: number; ms: number }, b: { y: number; m: number; d: number; hh: number; mm: number; ss: number; ms: number }) {
  const keys = ["y", "m", "d", "hh", "mm", "ss", "ms"] as const;
  for (const k of keys) {
    if (a[k] !== b[k]) return a[k] < b[k] ? -1 : 1;
  }
  return 0;
}

function toOffsetIsoForZone(date: Date, timeZone: string): string {
  const wall = formatWallParts(date, timeZone);
  const asUtc = Date.UTC(wall.y, wall.m - 1, wall.d, wall.hh, wall.mm, wall.ss, wall.ms);
  const offsetMin = Math.round((asUtc - date.getTime()) / 60000);
  const sign = offsetMin >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMin);
  return `${wall.y}-${pad(wall.m)}-${pad(wall.d)}` + `T${pad(wall.hh)}:${pad(wall.mm)}:${pad(wall.ss)}.${pad(wall.ms, 3)}` + `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
